/**
 * TEMPORARY — where the lab-curve request goes, and the same-origin proxy in front of it.
 * Full story, decisions and the removal checklist: `apps/sound-guys/docs/chart-data-proxy.md`.
 *
 * The measurement API lives on `soundguys-internal.netlify.app`. Some readers' networks block
 * `*.netlify.app` by hostname (seen from Laos, 2026-09-19: the TLS handshake is dropped on the
 * SNI), so a browser that calls it directly draws no lab chart anywhere on the site. The
 * browser therefore asks OUR host (`/api/chart-data`, `pages/api/chart-data.ts`), our server
 * asks Netlify — a server is on no reader's network — and Cloudflare caches the answer.
 *
 * Everything that exists only because of that detour is in this file, the API route and
 * their spec. When the measurement app is hosted on a domain of ours, the browser can call it
 * directly again and all three go: see "Removing it" in the doc.
 */
import { publicConfig } from 'config'

/** The measurement API itself. The one line to change when the app moves host. */
export const CHART_UPSTREAM_ORIGIN = 'https://soundguys-internal.netlify.app'
const CHART_UPSTREAM_PATH = '/api/chart-data'

/** Our same-origin front for it (`pages/api/chart-data.ts`). */
export const CHART_PROXY_ENDPOINT = '/api/chart-data'

/** The measurement sets the site asks for. A closed list: see `parseChartDataQuery`. */
const MEASUREMENT_SETS: readonly string[] = ['default']
const DEFAULT_SET = 'default'

/** Give up on a slow upstream rather than hold a server socket; the chart shows its retry
 *  line, which is what a blocked reader sees today. */
export const UPSTREAM_TIMEOUT_MS = 8_000

/**
 * What Cloudflare and the browser may keep, by outcome. Cloudflare only honours these once the
 * zone has the Cache Rule in the doc — `/api/*` JSON is not cached by default.
 *  - `curves`: a product's curves change only on a re-measure. Six hours at the edge keeps a
 *    correction from waiting a day; an hour in the browser covers a reading session.
 *  - `empty`: "nothing measured" (upstream 404, or 200 with an unusable body). Short, so a
 *    product the lab has just published shows up within minutes.
 *  - `failed`: never stored — an upstream blip must not blank a chart for hours.
 */
export const CHART_CACHE_CONTROL = {
  curves: 'public, max-age=3600, s-maxage=21600, stale-while-revalidate=86400',
  empty: 'public, max-age=300, s-maxage=600',
  failed: 'no-store',
} as const

/** The upstream request for one product. */
export function upstreamChartDataUrl(clearbuyId: number, measurementSet = DEFAULT_SET): string {
  return `${CHART_UPSTREAM_ORIGIN}${CHART_UPSTREAM_PATH}?id=${clearbuyId}&measurementSet=${measurementSet}`
}

/**
 * The URL `fetchAudioChart` asks. On the server (the PDP's `getServerSideProps`) that is the
 * upstream directly: the server is not behind a reader's network, and a hop through our own
 * route would only add latency. In the browser it is the proxy — with the app's trailing
 * slash, because `trailingSlash` applies to API routes and the slash-less URL costs a 308
 * before every fetch (same rule as `lib/header-nav/use-header-nav`).
 */
export function chartDataUrl(clearbuyId: number): string {
  if (typeof window === 'undefined') {
    return upstreamChartDataUrl(clearbuyId)
  }
  const endpoint = publicConfig('useTrailingSlash')
    ? `${CHART_PROXY_ENDPOINT}/`
    : CHART_PROXY_ENDPOINT
  return `${endpoint}?id=${clearbuyId}&measurementSet=${DEFAULT_SET}`
}

export interface ChartDataQuery {
  id: number
  measurementSet: string
}

function one(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value
}

/**
 * The proxy's two params, or null when they are not exactly what the site sends. A closed
 * shape on purpose: the route is public and cached, so an open query string would let any
 * crawler mint cache entries and upstream calls (the 2026-08-27 fake-param flood is the
 * precedent). An extra param is refused rather than ignored: the edge keys its cache on the
 * whole query string, so `?id=1&x=<anything>` would otherwise be a fresh upstream call each.
 */
export function parseChartDataQuery(
  query: Partial<Record<string, string | string[]>>
): ChartDataQuery | null {
  if (Object.keys(query).some(key => key !== 'id' && key !== 'measurementSet')) {
    return null
  }
  const id = one(query.id) ?? ''
  const measurementSet = one(query.measurementSet) ?? DEFAULT_SET
  if (!/^[1-9]\d{0,8}$/.test(id) || !MEASUREMENT_SETS.includes(measurementSet)) {
    return null
  }
  return { id: Number(id), measurementSet }
}

/** Whether an upstream 200 body carries a product: the same test `fetchAudioChart` applies,
 *  here only to pick the cache lifetime. */
export function hasChartData(body: unknown): boolean {
  return (
    typeof body === 'object' &&
    body !== null &&
    typeof (body as { prod_id?: unknown }).prod_id === 'number'
  )
}
